import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { UserIcon } from '@/shared/assets/icons/user-icon';
import { Alert } from '@/shared/components/ui/alert/alert';
import { FormDateTimePicker } from '@/shared/components/ui/form/components/form-date-time-picker/form-date-time-picker';
import { Submit } from '@/shared/components/ui/form/components/submit/submit';
import { FormContent } from '@/shared/components/ui/form/form-content';
import { FormContainer } from '@/shared/components/ui/form/form.styled';
import { TSelectedItem } from '@/shared/components/ui/selected-items-list/components/selected-item/selected-item.types';
import { SelectedItemsList } from '@/shared/components/ui/selected-items-list/selected-items-list';
import { TUpdateUserRoleLinkInputSchema } from '@/shared/features/rbac/components/update-user-role-link/update-user-role-link.types';
import { TUpdateUserRoleLinkFormProps } from '@/shared/features/rbac/components/update-user-role-link-form/update-user-role-link-form.types';
import { useRbacConfig } from '@/shared/features/rbac/context/rbac-config-context';
import { useDateUtils } from '@/shared/hooks/use-date-utils/use-date-utils';
import { getFullname } from '@/shared/utils/format';

export const UpdateUserRoleLinkForm: React.FC<TUpdateUserRoleLinkFormProps> = ({
  users,
  userRoles,
  onClose,
}) => {
  const { t } = useTranslation();
  const { formatLocalTime } = useDateUtils();
  const { userId } = useParams();
  const { useTenantSettings } = useRbacConfig();
  const { maxUserRoleExpirationDays } = useTenantSettings();
  const { control, getValues } =
    useFormContext<TUpdateUserRoleLinkInputSchema>();
  const links = useWatch({
    control,
    name: 'links',
    defaultValue: getValues('links') ?? [],
  });

  const isUserView = !!userId;
  const shouldDefineExpiration =
    !!maxUserRoleExpirationDays && maxUserRoleExpirationDays > 0;

  const getSelectedItems = (): TSelectedItem[] => {
    return links?.map((link, index) => {
      const user = users?.find(({ userId }) => userId === link.userId);
      const role = userRoles?.find(
        ({ userRoleId }) => userRoleId === link.userRoleId,
      );
      const timestamp = link.expirationTimestamp;
      const missingTimestampError = shouldDefineExpiration && !timestamp;
      return {
        icon: <UserIcon />,
        name: isUserView ? role?.name : getFullname(user),
        actions: (
          <FormDateTimePicker<TUpdateUserRoleLinkInputSchema>
            futureOnly
            name={`links.${index}.expirationTimestamp`}
            maxDays={maxUserRoleExpirationDays}
            label={t('rbac:set_expiration_date')}
          />
        ),
        hint: timestamp
          ? t('rbac:role_expiration_hint', {
              date: timestamp && formatLocalTime(timestamp),
            })
          : undefined,
        variant: missingTimestampError ? 'error' : undefined,
        error: missingTimestampError
          ? t('ui:form.field_required', {
              field: t('common:expiration_date'),
            })
          : undefined,
      };
    });
  };

  const maxExpirationAlert = shouldDefineExpiration
    ? t('rbac:max_user_role_expiration_days_hint', {
        maxUserRoleExpirationDays,
      })
    : undefined;

  return (
    <FormContainer>
      <FormContent>
        {maxExpirationAlert && <Alert color="blue" text={maxExpirationAlert} />}
        <SelectedItemsList
          title={
            isUserView ? t('rbac:selected_roles') : t('rbac:selected_users')
          }
          selectedItems={getSelectedItems()}
        />
      </FormContent>
      <Submit
        onClose={onClose}
        submitButtonLabel={t('common:save_changes')}
        formHasToBeDirty
      />
    </FormContainer>
  );
};
